import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AppConfig } from '@app/core/app.config';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Asset, RelatedAsset } from '@app/core/model/entities/asset/asset';
import { LeaseInput } from '@app/core/model/entities/asset/lease';
import { OccupantInput, RelatedOccupant } from '@app/core/model/entities/asset/occupant';
import { Space } from '@app/core/model/entities/asset/space';
import { SECTION_ENTITY_TYPE_INJECTION } from '@app/core/model/other/field-config';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { SpacesService } from '@app/features/main/views/organization-spaces/spaces.service';
import { eq } from '@app/shared/extra/filter-model-builder';
import { getLocale } from '@app/shared/extra/utils';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { GeneratesObject } from '@app/shared/interfaces/generates-object';
import { ExtraValidators } from '@app/shared/validators/extra-validators.module';
import { TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';
import { GridApi, GridOptions, RowSelectedEvent } from 'ag-grid-community';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { map, switchMap, toArray } from 'rxjs/operators';

@Component({
  templateUrl: './organization-lease-create-modal.component.html',
  styleUrls: [
    '../../../../../../shared/components/form-builder/sections/create-modal.component.scss',
    'organization-lease-create-modal.component.scss'
  ],
  standalone: false,
  providers: [
    {provide: SECTION_ENTITY_TYPE_INJECTION, useValue: EntityTypeEnum.LEASE}
  ]
})
export class OrganizationLeaseCreateModalComponent implements OnInit, OnDestroy, GeneratesObject {

  protected readonly EntityTypeEnum = EntityTypeEnum;

  public leaseForm: FormGroup<{
    newOccupant: FormControl<boolean>,
    occupantName: FormControl<string>,
    occupant: FormControl<RelatedOccupant>,
    asset: FormControl<RelatedAsset>,
    spaces: FormControl<Space[]>
  }>;
  protected missingRequiredFields$: Observable<{ code: string, label: string }[]>;

  // Ag-grid
  public gridApi: GridApi;
  public gridOptions: GridOptions;

  // Data
  public Asset = Asset;
  public spaces = new BehaviorSubject<Space[]>([]);

  protected assetTypeAllowsSpaceModule = false;

  private invalidRows: string[] = [];

  protected optionsGridService = inject(GridOptionsService);
  public appConfig = inject(AppConfig);
  protected fb = inject(FormBuilder);
  protected assetsService = inject(AssetsService);
  protected spacesService = inject(SpacesService);
  protected columnBuilder = inject(ColumnBuilder);
  protected translate: TranslateService = inject(TranslateService);
  protected accessManager = inject(AccessManager);
  protected destroyRef = inject(DestroyRef);

  constructor() {
    this.gridOptions = {
      ...this.optionsGridService.staticSelectSpacesGridOptions,
      defaultColDef: {
        resizable: true,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        filter: true
      },
      treeData: true,
      localeText: this.columnBuilder.localeTextCommon(),
      domLayout: 'autoHeight',
      rowClassRules: {
        'invalid-row': ({data}: { data: Space }): boolean => this.invalidRows.includes(data.id)
      },
      onRowClicked: this.onRowSelected.bind(this),
      onRowSelected: this.onRowSelected.bind(this)
    };

    // Init leaseForm
    this.leaseForm = this.fb.group({
      newOccupant: this.fb.control(false),
      occupantName: this.fb.control(null, Validators.compose([
        ExtraValidators.requiredConditional(() => this.leaseForm.controls.newOccupant.value),
        Validators.maxLength(this.appConfig.FIELD_MAX_LENGTH)
      ])),
      occupant: this.fb.control(null, ExtraValidators.requiredConditional(() => !this.leaseForm.controls.newOccupant.value)),
      asset: this.fb.control(null, Validators.required),
      spaces: this.fb.control([], Validators.compose([
        ExtraValidators.checkSpaces
      ]))
    });

    const missingRequiredFields = [
      {code: 'assetId', label: this.translate.instant('LABEL.ASSET') as string},
      {code: 'spaceIds', label: this.translate.instant('LABEL.SPACES') as string},
    ]
      .filter(({code}) => !this.accessManager.hasFieldWriteOnCreateAccess(code, EntityTypeEnum.LEASE))
      .concat(
        [{code: 'name', label: this.translate.instant('LABEL.OCCUPANT_NAME') as string}]
          .filter(({code}) => !this.accessManager.hasFieldWriteOnCreateAccess(code, EntityTypeEnum.OCCUPANT))
      );

    this.missingRequiredFields$ = from(missingRequiredFields).pipe(
      map(({code, label}) => ({code, label})),
      toArray()
    );
  }

  /**
   * Fetch possible values for accessible Assets and Spaces.
   */
  public ngOnInit(): void {
    // Listen for Spaces to be displayed in the grid
    this.leaseForm.controls.asset.valueChanges
      .pipe(
        switchMap(asset => {
          this.assetTypeAllowsSpaceModule = asset?.typeModules.includes(EntityTypeEnum.SPACE);
          if (this.assetTypeAllowsSpaceModule) {
            this.leaseForm.controls.spaces.addValidators(Validators.required);
            const selectParams: SelectParams = {
              locale: getLocale(),
              filters: [
                eq('assetId', asset.id)
              ]
            };
            return this.spacesService.loadAllSpaces(selectParams);
          }
          this.leaseForm.controls.spaces.removeValidators(Validators.required);
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.spaces);

    // Update conditional required validators when the toggle slider changes
    this.leaseForm.controls.newOccupant.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newOccupant => {
        this.leaseForm.controls.occupantName.setValidators(
          ExtraValidators.requiredConditional(() => newOccupant)
        );
        this.leaseForm.controls.occupant.setValidators(
          ExtraValidators.requiredConditional(() => !newOccupant)
        );
        this.leaseForm.controls.occupantName.updateValueAndValidity();
        this.leaseForm.controls.occupant.updateValueAndValidity();
      });
  }

  /**
   * Stop Observable subscriptions.
   */
  public ngOnDestroy(): void {
    this.spaces.complete();
  }

  /**
   * Load the grid's column definitions and fill with the selected Asset's Spaces.
   * @param params Ag-grid params.
   */
  public onGridReady(params): void {
    this.gridApi = params.api;
    this.gridApi.applyColumnState({
      state: [
        {colId: 'ag-Grid-AutoColumn', sort: 'asc'}
      ]
    });

    // Resize columns when receiving new Spaces
    this.spaces.subscribe(spaces => {
      this.gridApi.setGridOption('rowData', spaces);
      this.gridApi.sizeColumnsToFit();
      this.leaseForm.controls.spaces.reset([]);
    });
  }

  /**
   * Return an object containing data from the filled form.
   * @return Data for creating Occupant and Lease.
   */
  public getGeneratedObject(): { occupantInput?: OccupantInput, leaseInput: LeaseInput } {
    return {
      occupantInput: {
        name: this.leaseForm.controls.occupantName.value
      },
      leaseInput: {
        occupantId: this.leaseForm.controls.occupant.value?.id,
        assetId: this.leaseForm.controls.asset.value?.id,
        spaceIds: this.leaseForm.controls.spaces.value.map(space => space.id)
      }
    };
  }

  /**
   * Update value in the form and refresh invalid rows.
   * @param event Event thrown by the grid.
   * @private
   */
  private onRowSelected(event: RowSelectedEvent): void {
    const spaces = this.gridApi.getSelectedRows() as Space[];
    this.leaseForm.controls.spaces.setValue(spaces);
    this.leaseForm.controls.spaces.markAsTouched();

    const nodesToRefresh = [event.node];

    // Check if row is invalid and update invalid rows
    if (event.node.isSelected() && (this.hasParentSelected(event.node.data) || this.hasChildrenSelected(event.node.data))) {
      this.invalidRows.push(event.node.data.id);
    } else if (!event.node.isSelected()) {
      this.invalidRows = this.invalidRows.filter(row => {
        const rowNode = this.gridApi.getRowNode(row);
        nodesToRefresh.push(rowNode);
        return row !== event.node.data.id
          && (this.hasParentSelected(rowNode.data) || this.hasChildrenSelected(rowNode.data));
      });
    }

    // Refresh rows
    this.gridApi.redrawRows({
      rowNodes: nodesToRefresh
    });
  }

  /**
   * Checks if a Space has ancestors that are selected in the grid.
   * @param space Space to check.
   * @return True if at least one ancestor is selected, false otherwise.
   * @private
   */
  private hasParentSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => space.parentPath.includes(selectedRow.id));
  }

  /**
   * Checks if a Space has children that are selected in the grid.
   * @param space Space to check.
   * @return True is at least one child is selected, false otherwise.
   * @private
   */
  private hasChildrenSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => selectedRow.parentPath.includes(space.id));
  }
}
